import type { PulsoAceleracao } from '../../dominio/didatico/metricasCinematica'

interface Props {
  pulso: PulsoAceleracao
  tempoNormalizado: number
  comCinto: boolean
}

/**
 * Mini-gráfico em SVG do pulso ilustrativo de desaceleração (em g) ao longo
 * dos milissegundos do impacto. O pulso é parametrizado, não medido na cena;
 * por isso não há faixas de referência que pareçam limites clínicos.
 */
export function MiniGraficoForcaG({ pulso, tempoNormalizado, comCinto }: Props) {
  const largura = 260
  const altura = 90
  const margemX = 28
  const margemY = 16
  const wUtil = largura - margemX - 8
  const hUtil = altura - margemY - 14

  // Escala do eixo: o próprio pico com folga
  const gMaxVisual = Math.max(10, Math.ceil(pulso.gPico * 1.15))

  const pontos = pulso.curva
  if (pontos.length < 2) return null

  // Gera o caminho SVG
  const pathD = pontos.reduce((acc, p, idx) => {
    const x = margemX + p.tempoNormalizado * wUtil
    const y = altura - 14 - (p.g / gMaxVisual) * hUtil
    return idx === 0 ? `M ${x.toFixed(1)} ${y.toFixed(1)}` : `${acc} L ${x.toFixed(1)} ${y.toFixed(1)}`
  }, '')

  // Ponto atual na linha do tempo
  const xAtual = margemX + Math.min(1, Math.max(0, tempoNormalizado)) * wUtil
  const yAtual = altura - 14 - (pulso.gAtual / gMaxVisual) * hUtil

  // Ponto de pico
  const xPico = margemX + (pulso.tempoPicoMs / pulso.duracaoTotalMs) * wUtil
  const yPico = altura - 14 - (pulso.gPico / gMaxVisual) * hUtil

  const corCurva = comCinto ? '#10b981' : '#ef4444'

  return (
    <div className="mini-grafico-g-container">
      <div className="cabecalho-grafico-g">
        <span className="rotulo-grafico">Pulso ilustrativo de desaceleração (g)</span>
        <span className="badge-g normal">Pico ilustrativo: {pulso.gPico} g</span>
      </div>

      <svg
        viewBox={`0 0 ${largura} ${altura}`}
        className="svg-curva-g"
        role="img"
        aria-label={`Pulso ilustrativo de desaceleração. Pico de ${pulso.gPico} g, não medido na cena.`}
      >
        {/* Linhas de grade e referências */}
        <line
          x1={margemX}
          y1={altura - 14}
          x2={largura - 8}
          y2={altura - 14}
          stroke="#475569"
          strokeWidth="1"
        />
        <text x={largura - 8} y={altura - 3} textAnchor="end" fill="#94a3b8" fontSize="8">
          {pulso.duracaoTotalMs} ms
        </text>
        <text x={margemX} y={altura - 3} textAnchor="start" fill="#94a3b8" fontSize="8">
          0 ms
        </text>

        {/* Área preenchida sob a curva */}
        <path
          d={`${pathD} L ${largura - 8} ${altura - 14} L ${margemX} ${altura - 14} Z`}
          fill={corCurva}
          fillOpacity="0.15"
        />

        {/* Linha da curva */}
        <path d={pathD} fill="none" stroke={corCurva} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />

        {/* Ponto de pico marcado */}
        <circle cx={xPico} cy={yPico} r="3" fill={corCurva} />

        {/* Cursor do instante atual */}
        <line x1={xAtual} y1={margemY} x2={xAtual} y2={altura - 14} stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="3 2" />
        <circle cx={xAtual} cy={yAtual} r="4" fill="#38bdf8" stroke="#0f172a" strokeWidth="1.5" />
      </svg>

      <div className="rodape-grafico-g">
        <span>Desaceleração no instante: <strong>{pulso.gAtual} g</strong></span>
        <span className="interpretacao-g">{pulso.interpretacao}</span>
      </div>
      <small className="interpretacao-g">
        Curva ilustrativa, com valores predefinidos; não é medição desta colisão.
      </small>
    </div>
  )
}
